import { useState, type KeyboardEvent } from 'react';
import { X } from 'lucide-react';
import { cn } from '@/utils/cn';

export function TokenChips({
  label,
  tokens,
  onChange,
  placeholder,
  disabled,
}: {
  label: string;
  tokens: string[];
  onChange: (next: string[]) => void;
  placeholder: string;
  disabled?: boolean;
}) {
  const [draft, setDraft] = useState('');

  const add = () => {
    if (disabled) return;
    const next = draft.trim().toLowerCase();
    if (!next || tokens.includes(next)) {
      setDraft('');
      return;
    }
    onChange([...tokens, next]);
    setDraft('');
  };

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (disabled) return;
    if (event.key === 'Enter' || event.key === ',') {
      event.preventDefault();
      add();
    }
    if (event.key === 'Backspace' && !draft && tokens.length) {
      onChange(tokens.slice(0, -1));
    }
  };

  return (
    <div>
      <label className="mb-1.5 block text-label-md font-semibold uppercase tracking-[0.08em] text-foreground-subtle">
        {label}
      </label>
      <div
        className={cn(
          'flex min-h-[42px] flex-wrap items-center gap-1.5 rounded-lg border border-border bg-surface px-2 py-1.5',
          disabled && 'pointer-events-none opacity-60'
        )}
      >
        {tokens.map((token) => (
          <span
            key={token}
            className="inline-flex items-center gap-1 rounded-pill border border-brand/20 bg-brand-subtle px-2 py-0.5 text-body-sm text-brand-deep"
          >
            {token}
            <button
              type="button"
              aria-label={`Remove ${token}`}
              className="rounded-full p-0.5 hover:bg-brand/10"
              onClick={() => onChange(tokens.filter((item) => item !== token))}
            >
              <X className="h-3 w-3" />
            </button>
          </span>
        ))}
        <input
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={onKeyDown}
          onBlur={add}
          disabled={disabled}
          placeholder={tokens.length ? '' : placeholder}
          className="min-w-[8rem] flex-1 bg-transparent px-1 py-1 text-body-md text-foreground outline-none placeholder:text-foreground-subtle"
        />
      </div>
    </div>
  );
}
